import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import FieldWrapper from './FieldWrapper';
import InFieldConfirmationValue from '../InFieldConfirmationValue';

import {
    german2isoDate,
    standardizeDateFormat,
    browserDateToGermanDateFormat
} from '../../../../src/util/date_time_handler';
import {
    isRequiredFulfilled,
    isTextDateFormatValid,
    isTimeSpanPartValid
} from '../util/input_checks'
import { 
    useHandleInputChange,
    useAreRequiredErrorsActive
} from '../context-provider/InputFormContextProvider';
import { useIsConfirmationViewOpenContext } from '../context-provider/FormBaseContextProvider';
import FieldErrorMsgs from '../util/input_form_fields_error_msgs.json';


const createTopInfoText = (str_minDate, str_maxDate, b_displayTopInfoText) => {
    /**
     * Creates a top info messag for min. and max. date value if present.
     * :Returns
     *  Info messages (str)
     */
    if (!b_displayTopInfoText) return '';
    const str_minMsg = str_minDate ?
        'Min. Datum: ' + browserDateToGermanDateFormat(str_minDate) + ', ' : '';
    const str_maxMsg = str_maxDate ?
        'Max. Datum: ' + browserDateToGermanDateFormat(str_maxDate) + '' : '';
    return str_minMsg + str_maxMsg;
}

const REQUIRED_EMSG = FieldErrorMsgs.required;
const DATE_FORMAT_EMSG = FieldErrorMsgs.datetime.date.format;
const MIN_DATE_EMSG = FieldErrorMsgs.datetime.date.tooSmall;
const MAX_DATE_EMSG = FieldErrorMsgs.datetime.date.tooBig;

const DateField = ({    
    str_id,
    str_fieldTitle='',
    str_date='',     /* Received date formats are of form: YYYY-MM-DD (DB format). */
    str_minDate,     /* Input format: yyyy-mm-dd (e.g. 2022-07-27). */
    str_maxDate,     /* Input format: yyyy-mm-dd (e.g. 2024-07-28). */
    str_errorMsg='', /* A different error message can be served from the span fields parent. */
    str_bottomInfoText='',
    b_displayTopInfoText=true,
    b_displayBottomInfoText=true,
    b_isRequired=false,
    b_isSpanField=false,
    b_isStartDate=false,          /* Determines if component is start or end field. */
    b_hasSpanChanged=false,       /* Switches between true/false to check for changes. */
    fct_isDateSpanPartValid=null, /* Function for parent component (span field). */
    fct_sendDataToParent=null     /* Required for span field. */
}) => {

    const [date, setDate] = useState(str_date)
    const [errorMsg, setErrorMsg] = useState(str_errorMsg)
    const dateInputFieldRef = useRef()
    
    const handleInputChange = useHandleInputChange()
    const isRequiredErrorActivate = useAreRequiredErrorsActive()
    const isConfirmationViewOpen = useIsConfirmationViewOpenContext()

    const str_topInfoText = createTopInfoText(str_minDate, str_maxDate, b_displayTopInfoText)

    const isMinMaxValid = (currentDate) => {
        if (!currentDate) return true

        if (currentDate < str_minDate) {
            setErrorMsg(MIN_DATE_EMSG)
            return false
        } else if (str_maxDate && currentDate > str_maxDate) {
            setErrorMsg(MAX_DATE_EMSG)
            return false
        }
        return true
    }

    const hasError = () => {
        if (b_isRequired && !isRequiredFulfilled(date)) {
            setErrorMsg(REQUIRED_EMSG)
            return true
        }
        
        /* Check text input. */
        if (date !== '') {
            let str_datePickerFormat;
            if (dateInputFieldRef.current.type === 'text') {
                if (isTextDateFormatValid(date)) {
                    const str_stdDate = standardizeDateFormat(date)
                    str_datePickerFormat = german2isoDate(str_stdDate)
                    setDate(str_stdDate)
                } else {
                    setErrorMsg(DATE_FORMAT_EMSG)
                    return true
                }
            } else {
                str_datePickerFormat = date
            }
            if (!isMinMaxValid(str_datePickerFormat)) return true
        }
        
        /* Clear the error message if all the above checks are valid (input is valid). 
         * The last check concerns the span and creates a specific error message if 
         * it is invalid. */
        setErrorMsg('')

        /* Check span input. */
        if (fct_isDateSpanPartValid)
            if (!isTimeSpanPartValid(fct_isDateSpanPartValid, date, b_isStartDate)) 
                return true
        
        return false
    }

    useMemo(() => {
        /* All the required fields that are blank and do not show erros,
         * are activated when a form is submitted. The line below is only 
         * executed once. After the first execution, the required field 
         * displays error messages until the input is correct. */
        if (isRequiredErrorActivate && b_isRequired) {
            /* Setting the error does not work w/o the setTimeout. 
             * Only the time fields have this issue ... */
            setTimeout(() => hasError(), 0)
        }
    }, [isRequiredErrorActivate])

    useMemo(() => {
        /* If new input is received, update the current char state. */
        setDate(str_date)
    }, [str_date])
    
    useEffect(() => {
        /* This is specifically necessary for the span fields. As the
         * parent can pass the error message down to the (this) child. 
         * Error messages that come from the span fiel parent, are only 
         * used to overwrite the error messages of the child if the child
         * does not have error messages or if it contains the word 
         * 'Zeitspanne'. Error messages of children have a higher priority. 
        */
        if (!errorMsg) setErrorMsg(str_errorMsg)
        else if (errorMsg.includes('Zeitspanne')) setErrorMsg(str_errorMsg)
    }, [b_hasSpanChanged])

    const handleOnBlur = (e) => {
        const b_hasError = hasError()
        const value = b_hasError ? e.target.value : 
                      german2isoDate(standardizeDateFormat(e.target.value))
        if (b_isSpanField) {
            /* This is the data span field parents request. */
            fct_sendDataToParent({
                isStart: b_isStartDate,
                value: value,
                hasError: b_hasError,
            })
        } else {
            handleInputChange({
                id: str_id,
                value: value,
                hasError: b_hasError
            })
        }
    }

    const removeDateOnClick = () => {
        /* Handles behaviour when user click on the remove date element. */
        
        setDate('')
        
        if (b_isSpanField) {
            /* This is the data span field parents request. */
            if (b_isRequired && isTimeSpanPartValid(fct_isDateSpanPartValid, '', b_isStartDate))
                setErrorMsg(REQUIRED_EMSG)
            
            fct_sendDataToParent({
                isStart: b_isStartDate,
                value: '',
                hasError: b_isRequired ? true : false
            })
        } else {
            if (b_isRequired) setErrorMsg(REQUIRED_EMSG)
            
            handleInputChange({
                id: str_id,
                value: '',
                hasError: b_isRequired ? true : false
            })
        }
    }

    return (
        isConfirmationViewOpen
        ?
        <FieldWrapper
            str_fieldTitle={str_fieldTitle}
            b_isRequired={false}
        >
            <InFieldConfirmationValue value={browserDateToGermanDateFormat(date)} />
        </FieldWrapper>
        :
        <FieldWrapper
            str_fieldTitle={str_fieldTitle}
            str_errorMsg={errorMsg}
            str_topInfoText={str_topInfoText}
            str_bottomInfoText={str_bottomInfoText}
            b_displayBottomInfoText={b_displayBottomInfoText}
            b_isRequired={b_isRequired}
        >
            <div className="in-field in-field-timing in-field-date in-field-timing-single">
                <input
                    value={date}
                    ref={dateInputFieldRef}
                    id={str_id}
                    type="date"
                    name="date-field"
                    placeholder='DD.MM.YYYY'
                    min={str_minDate}
                    max={str_maxDate}
                    required={b_isRequired}
                    onChange={() => setDate(dateInputFieldRef.current.value)}
                    onBlur={e => handleOnBlur(e)}
                />
                <div className=
                    {`remove-input remove-date-time ${date !== '' ? '' : 'hidden'}`}
                >
                    <span onClick={removeDateOnClick}>Datum löschen</span>
                </div>
            </div>
        </FieldWrapper>
    )
}

DateField.propTypes = {
    str_id: PropTypes.string,
    str_fieldTitle: PropTypes.string,
    str_date: PropTypes.string,
    str_minDate: PropTypes.string,
    str_maxDate: PropTypes.string,
    str_errorMsg: PropTypes.string,
    str_bottomInfoText: PropTypes.string,
    b_displayTopInfoText: PropTypes.bool,
    b_displayBottomInfoText: PropTypes.bool,
    b_isSpanField: PropTypes.bool,
    b_isRequired: PropTypes.bool,
    b_isStartDate: PropTypes.bool,
    b_hasSpanChanged: PropTypes.bool,
    fct_isDateSpanPartValid: PropTypes.func,
    fct_sendDataToParent: PropTypes.func
}

export default DateField
